/**
 * Shared rules for table requests and private dining enquiries.
 * Used by the forms in the browser and again by the API routes,
 * so both sides always agree on what is valid.
 */

export const RESTAURANT_TIME_ZONE = 'Europe/London'

export const MIN_PARTY = 1
export const MAX_PARTY = 12
export const MIN_EVENT_GUESTS = 6
export const MAX_EVENT_GUESTS = 120
export const MAX_DAYS_AHEAD = 90
export const MAX_EVENT_DAYS_AHEAD = 365
export const LAST_SEATING_BEFORE_CLOSE = 90

export const EVENT_TYPES = [
    "Family celebration",
    "Business dinner",
    "Full venue buyout",
    "Something else"
] as const

export type EventType = typeof EVENT_TYPES[number]

export type ReservationInput = {
    date: string
    time: string
    guests: number
    name: string
    phone: string
    requests: string
}

export type EnquiryInput = {
    name: string
    email: string
    phone: string
    eventType: string
    date: string
    guests: number
    message: string
}

export type FieldErrors<T> = Partial<Record<keyof T, string>>

export type RestaurantNow = {
    date: string
    minutes: number
}

export const restaurantNow = (moment: Date = new Date()): RestaurantNow => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: RESTAURANT_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(moment)

    const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find(part => part.type === type)?.value ?? '00'

    return {
        date: `${get('year')}-${get('month')}-${get('day')}`,
        minutes: Number(get('hour')) * 60 + Number(get('minute'))
    }
}

export const isIsoDate = (value: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false

    const parsed = new Date(`${value}T00:00:00Z`)

    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export const addDays = (isoDate: string, days: number): string => {
    const [year, month, day] = isoDate.split('-').map(Number)

    return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

const weekday = (isoDate: string): number => new Date(`${isoDate}T00:00:00Z`).getUTCDay()

export const closingMinutes = (isoDate: string): number => {
    const day = weekday(isoDate)

    if (day === 0) return 22 * 60
    if (day === 5 || day === 6) return 24 * 60

    return 23 * 60
}

const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60

    return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

/**
 * Seating times run every half hour from 12:00 until ninety minutes
 * before closing. Today's earlier times drop away an hour ahead.
 */
export const timeSlots = (isoDate: string, now?: RestaurantNow): Array<string> => {
    const valid = isIsoDate(isoDate)
    const close = valid ? closingMinutes(isoDate) : 23 * 60
    const last = close - LAST_SEATING_BEFORE_CLOSE
    const today = valid && now && isoDate === now.date
    const earliest = today ? now.minutes + 60 : 0
    const slots: Array<string> = []

    for (let minutes = 12 * 60; minutes <= last; minutes += 30) {
        if (minutes >= earliest) slots.push(formatMinutes(minutes))
    }

    return slots
}

export const formatLongDate = (isoDate: string): string => {
    if (!isIsoDate(isoDate)) return isoDate

    return new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC'
    }).format(new Date(`${isoDate}T00:00:00Z`))
}

export const normalisePhone = (value: string): string => value.replace(/[\s().-]/g, '')

const isPhone = (value: string): boolean => /^\+?\d{7,15}$/.test(normalisePhone(value))

const isEmail = (value: string): boolean => value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)

export const validateReservation = (input: ReservationInput, now: RestaurantNow = restaurantNow()): FieldErrors<ReservationInput> => {
    const errors: FieldErrors<ReservationInput> = {}

    if (!isIsoDate(input.date)) {
        errors.date = "Choose a date."
    }
    else if (input.date < now.date) {
        errors.date = "That date has passed. Choose another."
    }
    else if (input.date > addDays(now.date, MAX_DAYS_AHEAD)) {
        errors.date = "We take requests up to 90 days ahead. For later dates, call us."
    }

    if (!input.time) {
        errors.time = "Choose a time."
    }
    else if (!errors.date && !timeSlots(input.date, now).includes(input.time)) {
        errors.time = "That time is not available on this day. Choose another."
    }

    if (!Number.isInteger(input.guests) || input.guests < MIN_PARTY || input.guests > MAX_PARTY) {
        errors.guests = "Tables are for 1 to 12 guests. For larger groups, see Private Dining."
    }

    const name = input.name.trim()

    if (name.length < 2) errors.name = "Tell us your name."
    else if (name.length > 80) errors.name = "Keep your name under 80 characters."

    if (!input.phone.trim()) errors.phone = "We need a number to confirm your table."
    else if (!isPhone(input.phone)) errors.phone = "That number does not look right. Include the area code."

    if (input.requests.length > 500) errors.requests = "Keep special requests under 500 characters."

    return errors
}

export const validateEnquiry = (input: EnquiryInput, now: RestaurantNow = restaurantNow()): FieldErrors<EnquiryInput> => {
    const errors: FieldErrors<EnquiryInput> = {}
    const name = input.name.trim()
    const email = input.email.trim()

    if (name.length < 2) errors.name = "Tell us your name."
    else if (name.length > 80) errors.name = "Keep your name under 80 characters."

    if (!email) errors.email = "We reply by email, so we need yours."
    else if (!isEmail(email)) errors.email = "That email does not look right."

    if (input.phone.trim() && !isPhone(input.phone)) errors.phone = "That number does not look right. Include the area code."

    if (!(EVENT_TYPES as ReadonlyArray<string>).includes(input.eventType)) errors.eventType = "Choose the kind of evening."

    if (!isIsoDate(input.date)) {
        errors.date = "Choose a date."
    }
    else if (input.date < now.date) {
        errors.date = "That date has passed. Choose another."
    }
    else if (input.date > addDays(now.date, MAX_EVENT_DAYS_AHEAD)) {
        errors.date = "We plan events up to a year ahead."
    }

    if (!Number.isInteger(input.guests) || input.guests < MIN_EVENT_GUESTS || input.guests > MAX_EVENT_GUESTS) {
        errors.guests = "Private dining is for 6 to 120 guests."
    }

    if (input.message.length > 2000) errors.message = "Keep your message under 2,000 characters."

    return errors
}

export const eventPhrase = (eventType: string): string => {
    switch (eventType) {
        case 'Family celebration':
            return "a family celebration"
        case 'Business dinner':
            return "a business dinner"
        case 'Full venue buyout':
            return "a full venue buyout"
        default:
            return "a private evening"
    }
}
